/**
 * Evolución mensual y anual en barras (US4)
 *
 * Mensual: últimos 12 meses, cada uno junto al mismo mes del año anterior.
 * Anual: una barra por año desde el primer recuerdo.
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import type { DashboardTrends, TrendGranularity, TrendMetric } from '@/types/dashboard';
import { DeltaBadge } from './DeltaBadge';
import { formatNumber } from './format';
import { METRIC_OPTIONS, buildBars } from './trendData';
import { prefersReducedMotion } from './useCountUp';

interface TrendBarsProps {
  trends: DashboardTrends;
  granularity: TrendGranularity;
  onGranularityChange: (granularity: TrendGranularity) => void;
}

const GRANULARITY_OPTIONS: { value: TrendGranularity; label: string }[] = [
  { value: 'month', label: 'Mensual' },
  { value: 'year', label: 'Anual' },
];

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'min-h-11 rounded-md px-3 py-2 text-sm font-light transition-all',
            value === option.value
              ? 'bg-[#1e1e1e] text-white'
              : 'border border-gray-200 bg-white text-[#1e1e1e] hover:bg-gray-50'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function TrendBars({ trends, granularity, onGranularityChange }: TrendBarsProps) {
  const [metric, setMetric] = useState<TrendMetric>('memories');
  // Las barras crecen desde la base tras el primer cuadro (009, FR-003)
  const [grown, setGrown] = useState(prefersReducedMotion());
  useEffect(() => {
    if (grown) return;
    const frame = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(frame);
  }, [grown]);
  const scale = grown ? 1 : 0;
  const bars = buildBars(trends.points, granularity, metric);
  const max = Math.max(0, ...bars.flatMap((b) => [b.value, b.previousYear ?? 0]));
  const metricLabel = METRIC_OPTIONS.find((m) => m.value === metric)?.label ?? '';

  // Último mes cerrado frente al mes anterior y al mismo mes del año pasado
  let lastClosedIndex = -1;
  if (granularity === 'month') {
    for (let i = bars.length - 1; i >= 0; i -= 1) {
      if (!bars[i].partial) {
        lastClosedIndex = i;
        break;
      }
    }
  }
  const lastClosed = lastClosedIndex >= 0 ? bars[lastClosedIndex] : null;
  const monthBefore = lastClosedIndex > 0 ? bars[lastClosedIndex - 1] : null;

  return (
    <section className="space-y-4 rounded-sm border border-gray-200 bg-white p-6" aria-labelledby="trends-title">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h2 id="trends-title" className="text-[16px] font-light text-[#1e1e1e]">
          Evolución
        </h2>
        <SegmentedControl
          options={GRANULARITY_OPTIONS}
          value={granularity}
          onChange={onGranularityChange}
          label="Vista"
        />
      </div>

      <SegmentedControl options={METRIC_OPTIONS} value={metric} onChange={setMetric} label="Métrica" />

      {max === 0 ? (
        <p className="py-10 text-center text-sm font-light text-[#9d9c9c]">Sin datos suficientes</p>
      ) : (
        <>
          <div className="flex h-44 items-end gap-1 sm:gap-2" data-testid="trend-bars">
            {bars.map((bar) => (
              <div key={bar.key} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1">
                <span className="text-[10px] font-light text-[#9d9c9c]">{formatNumber(bar.value)}</span>
                <div className="flex h-full w-full items-end justify-center gap-0.5">
                  {granularity === 'month' && (
                    <div
                      className="w-1/3 max-w-[10px] rounded-t bg-gray-300 transition-[height] duration-700 ease-out motion-reduce:transition-none"
                      style={{ height: `${((bar.previousYear ?? 0) / max) * 100 * scale}%` }}
                      title={`Mismo mes del año anterior: ${bar.previousYear === null ? 'sin datos' : formatNumber(bar.previousYear)}`}
                      data-testid="previous-year-bar"
                    />
                  )}
                  <div
                    className={cn(
                      'max-w-[22px] rounded-t transition-[height] duration-700 ease-out motion-reduce:transition-none',
                      granularity === 'month' ? 'w-1/2' : 'w-2/3 max-w-[48px]',
                      bar.partial ? 'bg-[#1e1e1e]/40' : 'bg-[#1e1e1e]'
                    )}
                    style={{ height: `${(bar.value / max) * 100 * scale}%`, minHeight: bar.value > 0 ? 2 : 0 }}
                    title={`${bar.fullLabel}: ${formatNumber(bar.value)}`}
                  />
                </div>
                <span className="w-full truncate text-center text-[10px] font-light text-[#9d9c9c]">{bar.label}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4 text-xs font-light text-[#9d9c9c]">
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-3 rounded-sm bg-[#1e1e1e]" /> {metricLabel}
            </span>
            {granularity === 'month' && (
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-3 rounded-sm bg-gray-300" /> Mismo mes del año anterior
              </span>
            )}
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-3 rounded-sm bg-[#1e1e1e]/40" /> En curso
            </span>
          </div>

          {lastClosed && (
            <div className="space-y-1 border-t border-gray-100 pt-3" data-testid="last-closed-month">
              <p className="text-sm font-light text-[#1e1e1e]">
                {lastClosed.fullLabel.charAt(0).toUpperCase() + lastClosed.fullLabel.slice(1)}:{' '}
                {formatNumber(lastClosed.value)} {metricLabel.toLowerCase()}
              </p>
              {monthBefore && (
                <DeltaBadge current={lastClosed.value} previous={monthBefore.value} label={`vs. ${monthBefore.fullLabel}`} />
              )}
              <DeltaBadge
                current={lastClosed.value}
                previous={lastClosed.previousYear ?? 0}
                label="vs. el mismo mes del año anterior"
              />
            </div>
          )}
        </>
      )}
    </section>
  );
}
